// Typed wrappers for the PUBLIC refund portal (no auth).
// GET /api/v2/refund-requests/{token}  → lookup (form | status | unavailable)
// POST /api/v2/refund-requests/{token} → file the customer request
// Both paths exist in openapi.yaml and use the shared apiClient.
import { apiClient } from "@/lib/api/client";
import type { components, operations } from "@generated/api/schema";

// openapi-typescript emits the lookup union inline on the GET 200 response
// (no named `PublicRefundLookupResponse` schema), so derive it from there to
// stay locked to the endpoint contract.
export type RefundLookupResult =
  operations["lookup_refund_request_api_v2_refund_requests__token__get"]["responses"][200]["content"]["application/json"];
export type RefundFormLookup =
  components["schemas"]["PublicRefundFormResponse"];
export type RefundDetail = components["schemas"]["RefundRequestDetailResponse"];
export type RefundEvent = components["schemas"]["RefundRequestEventResponse"];
export type RefundStatus = RefundDetail["status"];
export type SubmitRefundBody = components["schemas"]["SubmitRefundRequest"];

/**
 * HTTP failure from the public refund endpoints, carrying the status so the
 * form can tell "link expired/spent" (404) apart from transient failures and
 * stop telling the customer to retry something that can never succeed (#189).
 */
export class RefundRequestHttpError extends Error {
  constructor(
    readonly status: number,
    operation: string,
    body: unknown,
  ) {
    super(`${operation} failed status=${status}: ${JSON.stringify(body)}`);
    this.name = "RefundRequestHttpError";
  }
}

/** Resolve a customer token into what the portal should render. */
export async function fetchRefundLookup(
  token: string,
): Promise<RefundLookupResult> {
  const { data, error, response } = await apiClient.GET(
    "/api/v2/refund-requests/{token}",
    { params: { path: { token } } },
  );
  if (error || !data)
    throw new RefundRequestHttpError(
      response.status,
      "fetchRefundLookup",
      error,
    );
  return data;
}

/** File a customer refund request against the token. Idempotent on resubmit. */
export async function submitRefundRequest(
  token: string,
  body: SubmitRefundBody,
): Promise<RefundDetail> {
  const { data, error, response } = await apiClient.POST(
    "/api/v2/refund-requests/{token}",
    { params: { path: { token } }, body },
  );
  if (error || !data)
    throw new RefundRequestHttpError(
      response.status,
      "submitRefundRequest",
      error,
    );
  return data;
}
